import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import Paper from "@mui/material/Paper";
import moment from "moment";
import classes from "../../styles/DashboardView.module.css";
import { WeekRevenueCompare } from "../../utilities/dashboardAggregations";
import { numberWithCommas } from "../Oversigt";
import { IndexBadge } from "./IndexBadge";

interface WeeklyRevenueChartProps {
    data: WeekRevenueCompare[];
    basisLabel: string;
    currentYearLabel: string;
    prevYearLabel: string;
    prev2YearLabel: string;
}

const COLOR_CURRENT = "#2e7d32";
const COLOR_PREV = "#81c784";
const COLOR_PREV2 = "#c8e6c9";

function LegendItem({ color, label, dashed = false }: { color: string; label: string; dashed?: boolean }) {
    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{
                display: "inline-block",
                width: 18,
                height: dashed ? 0 : 4,
                borderRadius: 1,
                background: dashed ? "transparent" : color,
                borderTop: dashed ? `2px dashed ${color}` : undefined,
                marginTop: dashed ? 3 : 0,
            }} />
            {label}
        </span>
    );
}

export function WeeklyRevenueChart({ data, basisLabel, currentYearLabel, prevYearLabel, prev2YearLabel }: WeeklyRevenueChartProps) {
    if (!data || data.length === 0) {
        return (
            <Paper className={classes.chartCard} elevation={2}>
                <div className={classes.chartTitle}>Omsætning pr. uge</div>
                <p>Ingen data</p>
            </Paper>
        );
    }

    const hasPrev2 = data.some(d => d.prev2Revenue > 0);

    return (
        <Paper className={classes.chartCard} elevation={2}>
            <div className={classes.chartHeader}>
                <h3 className={classes.chartTitle}>
                    Omsætning pr. uge <span className={classes.chartSubtitle}>· {basisLabel}</span>
                </h3>
                <div style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 12,
                    marginLeft: "auto",
                    fontSize: "0.75rem",
                    color: "#5f6368",
                    fontVariantNumeric: "tabular-nums",
                }}>
                    <LegendItem color={COLOR_CURRENT} label={currentYearLabel} />
                    <LegendItem color={COLOR_PREV} label={prevYearLabel} />
                    {hasPrev2 && <LegendItem color={COLOR_PREV2} label={prev2YearLabel} dashed />}
                </div>
            </div>
            <ResponsiveContainer width="100%" height={320}>
                <LineChart data={data} margin={{ top: 10, right: 16, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="weekNumber" tick={{ fontSize: 12 }} tickFormatter={(v) => `U${v}`} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${numberWithCommas(v)}`} />
                    <Tooltip content={({ active, payload }: any) => {
                        if (!active || !payload || !payload.length) return null;
                        const d: WeekRevenueCompare = payload[0].payload;
                        return (
                            <div style={{ background: "white", border: "1px solid #ccc", padding: 10, borderRadius: 4, minWidth: 220 }}>
                                <div style={{ fontWeight: 600, marginBottom: 2 }}>Uge {d.weekNumber}</div>
                                <div style={{ fontSize: 11, color: "#8a8f96", marginBottom: 6 }}>
                                    {moment(d.startDate).format("DD-MM-YY")} – {moment(d.endDate).format("DD-MM-YY")}
                                </div>
                                <div style={{ fontSize: 13, color: COLOR_CURRENT, fontWeight: 600 }}>{currentYearLabel}: {numberWithCommas(d.currentRevenue)} kr.</div>
                                <div style={{ fontSize: 12, color: COLOR_PREV, fontWeight: 600 }}>{prevYearLabel}: {numberWithCommas(d.previousRevenue)} kr.</div>
                                {hasPrev2 && (
                                    <div style={{ fontSize: 12, color: "#7cb380", fontWeight: 600 }}>{prev2YearLabel}: {numberWithCommas(d.prev2Revenue)} kr.</div>
                                )}
                                <IndexBadge current={d.currentRevenue} previous={d.previousRevenue} />
                            </div>
                        );
                    }} />
                    {hasPrev2 && (
                        <Line
                            type="monotone"
                            dataKey="prev2Revenue"
                            stroke={COLOR_PREV2}
                            strokeWidth={2}
                            strokeDasharray="5 4"
                            dot={{ r: 2 }}
                            activeDot={{ r: 5 }}
                            isAnimationActive={false}
                        />
                    )}
                    <Line type="monotone" dataKey="previousRevenue" stroke={COLOR_PREV} strokeWidth={2} dot={{ r: 2.5 }} activeDot={{ r: 5 }} isAnimationActive={false} />
                    <Line type="monotone" dataKey="currentRevenue" stroke={COLOR_CURRENT} strokeWidth={2.5} dot={{ r: 2.5 }} activeDot={{ r: 5 }} isAnimationActive={false} />
                </LineChart>
            </ResponsiveContainer>
        </Paper>
    );
}
